import { useCallback, useMemo, useState } from 'react';
import type { ArtistProfileResponse } from 'fym-shared/src/music24/artistProfile';
import type { ProfileSendService, ProfileSendTarget } from 'fym-shared/src/music24/profileSend';
import {
  useGetProfileSendOverviewQuery,
  useSaveProfileSendSettingsMutation,
  useStartProfileSendMutation,
} from '../../../../../redux/api/fymAPI';
import { useRealtimePollInterval } from '../../../../realtime/useRealtimePollInterval';
import { isSendWorking, sendAllFooter, sendAllLead, sendAllServices, type SendAllItemView } from './sendAllModel';

/** The poll while a send works and the socket is down (`profile.send.updated` covers the rest). */
const WORKING_POLL_MS = 4000;

/**
 * The data and the actions of "Send to all" (design profile-card c): the overview read, the tick
 * state (a draft of the sheet; every item that can go starts ticked), the rights tick, the send and
 * the sync switch. The sheet mounts only while it is open, so each open reads the overview again.
 */
export function useSendAll(
  accountId: number,
  profile: Pick<ArtistProfileResponse, 'bio'>,
  only: ProfileSendService | null = null
) {
  const [isWorkingNow, setWorkingNow] = useState(false);
  const pollingInterval = useRealtimePollInterval(isWorkingNow ? WORKING_POLL_MS : 0);
  const overview = useGetProfileSendOverviewQuery({ accountId }, { refetchOnMountOrArgChange: true, pollingInterval });
  const [startSend, startState] = useStartProfileSendMutation();
  const [saveSettings, settingsState] = useSaveProfileSendSettingsMutation();
  const [ticks, setTicks] = useState<Record<string, boolean>>({});
  const [rightsTicked, setRightsTicked] = useState(false);
  const [startFailed, setStartFailed] = useState(false);

  const { data } = overview;
  const services = useMemo(() => (data === undefined ? [] : sendAllServices(data, profile)), [data, profile]);
  const isWorking = data !== undefined && isSendWorking(data);
  if (isWorking !== isWorkingNow) {
    setWorkingNow(isWorking);
  }
  const footer = useMemo(() => sendAllFooter(services, ticks, isWorking, only), [isWorking, only, services, ticks]);
  const lead = useMemo(() => sendAllLead(services, isWorking), [isWorking, services]);
  const rightsAccepted = data?.rights.accepted === true;
  const rightsOk = rightsAccepted || rightsTicked;

  const toggle = useCallback((view: SendAllItemView, on: boolean) => {
    setTicks((before) => ({ ...before, [view.key]: on }));
  }, []);

  const send = useCallback(
    (targets: ProfileSendTarget[]) => {
      if (targets.length === 0 || !rightsOk) {
        return;
      }
      setStartFailed(false);
      startSend({ accountId, targets, rightsConfirmed: rightsAccepted ? undefined : true })
        .unwrap()
        .catch((error: unknown) => {
          console.error(error);
          setStartFailed(true);
        });
    },
    [accountId, rightsAccepted, rightsOk, startSend]
  );

  const setKeepInSync = useCallback(
    (keepInSync: boolean) => {
      saveSettings({ accountId, keepInSync }).unwrap().catch(console.error);
    },
    [accountId, saveSettings]
  );

  return {
    only,
    overview,
    services,
    ticks,
    footer,
    lead,
    isWorking,
    rightsAccepted,
    rightsTicked,
    rightsOk,
    setRightsTicked,
    toggle,
    send,
    isSending: startState.isLoading,
    startFailed,
    keepInSync: data?.keepInSync.enabled === true,
    setKeepInSync,
    isSavingSettings: settingsState.isLoading,
  };
}

export type SendAllState = ReturnType<typeof useSendAll>;
